import type { Map as OlMap } from 'ol'
import type { Layer } from 'ol/layer'
import Overlay from 'ol/Overlay'
import DOMPurify from 'dompurify'
import { ref, shallowRef, onUnmounted } from 'vue'

export interface VectorLayerMeta {
  popup_template: string
}

export interface PopupState {
  html: string
  visible: boolean
}

/** Matches {{ field_name }} — same regex pattern as the backend. */
const TEMPLATE_VAR_RE = /\{\{\s*(\w+)\s*\}\}/g

export function renderTemplate(template: string, properties: Record<string, unknown>): string {
  return template.replace(TEMPLATE_VAR_RE, (_, key: string) => {
    const val = properties[key]
    return val !== undefined && val !== null ? String(val) : 'none'
  })
}

/**
 * Attaches a single OL Overlay to the map and listens for singleclick
 * on vector features that belong to layers which have a schema.
 *
 * @param getLayerForFeature  callback that maps an OL layer to the matching
 *                            VectorLayer API object (so we can read its schema)
 */
export function useFeaturePopup(
  mapRef: ReturnType<typeof shallowRef<OlMap | null>>,
  overlayElementRef: ReturnType<typeof ref<HTMLElement | null>>,
  getVectorLayerMeta: (olLayer: Layer) => VectorLayerMeta | undefined,
) {
  const popup = ref<PopupState>({ html: '', visible: false })
  let overlay: Overlay | null = null

  function mount(map: OlMap) {
    if (!overlayElementRef.value) return
    overlay = new Overlay({
      element: overlayElementRef.value,
      autoPan: { animation: { duration: 200 } },
      positioning: 'bottom-center',
      stopEvent: true,   // clicks on the popup don't propagate to the map
      offset: [0, -10],
    })
    map.addOverlay(overlay)

    map.on('singleclick', (evt) => {
      let hit = false

      map.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
        if (hit) return          // take only the top-most feature
        const meta = getVectorLayerMeta(layer as Layer)
        if (!meta?.popup_template) return

        const props = (feature.getProperties() as Record<string, unknown>)
        // Feature properties are not fully trusted (CMS uploads / external GeoJSON),
        // and the result is injected via v-html — sanitize to strip scripts and
        // event handlers (e.g. onerror) while keeping the schema's table/markup.
        const html = DOMPurify.sanitize(renderTemplate(meta.popup_template, props))
        popup.value = { html, visible: true }
        overlay!.setPosition(evt.coordinate)
        hit = true
      })

      if (!hit) close()
    })
  }

  function close() {
    popup.value = { html: '', visible: false }
    overlay?.setPosition(undefined)
  }

  onUnmounted(() => {
    if (mapRef.value && overlay) mapRef.value.removeOverlay(overlay)
  })

  return { popup, mount, close }
}